import React, { useEffect, useState } from 'react';
import { getMelangeById } from '../data/melanges';
import { getEpiceById } from '../data/epices';
import ImageLoader from '../components/ImageLoader';
import Carousel from '../components/Carousel';
import '../styles/global.css';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Couleurs par mélange
const MELANGE_COLORS = {
  'rouge-obsidienne': '#9C0101',
  'bois-fauve': '#1A3713',
  'eveil-floral': '#728CC0',
};

// Import des vraies recettes depuis IdeesRecettes
const recettes = {
  'rouge-obsidienne': {
    couleur: '#9C0101',
    titre: 'Rouge Obsidienne',
    categories: [
      { nom: 'DIPS', recettes: [
        { titre: 'Houmous rouge volcan', description: 'Un houmous classique en apparence, mais transformé en une lave aromatique grâce au Rouge Obsidienne. Les pois chiches fondants se mêlent au tahini soyeux, relevés d\'un piment fumé profond. Chaque bouchée est une montée de chaleur lente, enveloppée dans une fumée presque boisée. À servir tiède avec pain plat ou légumes croquants.' },
        { titre: 'Crème de feta fouettée au piment fumé', description: 'L\'onctuosité d\'une feta fouettée avec un filet de yaourt grec, puis fouettée jusqu\'à devenir mousse aérienne. Rouge Obsidienne y insuffle une tension : fraîcheur lactée contre feu maîtrisé. À napper sur des pitas chaudes ou à glisser en fond de sandwich.' },
        { titre: 'Tartinade de haricots rouges & cumin', description: 'Imagine un chili réduit à sa quintessence, mais tartinable. Les haricots rouges sont mixés avec une pointe de citron, de cumin torréfié et Rouge Obsidienne : ça sent la braise et le soleil du sud-ouest. Un dip généreux, idéal avec des chips de maïs ou des légumes rôtis.' },
        { titre: 'Rillettes de sardines & citron au KAZA', description: 'La mer, le citron, et la braise. Les sardines sont écrasées à la fourchette avec un filet d\'huile d\'olive, du zeste de citron et une touche de Rouge Obsidienne. À tartiner sur des toasts ou à glisser dans des sandwichs méditerranéens. Puissant, iodé, addictif.' },
        { titre: 'Beurre pimenté fumé', description: 'Du beurre doux fouetté avec Rouge Obsidienne, du sel fumé et une pointe d\'ail. Il fond sur une tranche de pain chaud ou colore des légumes grillés. Un condiment simple, mais qui marque. Variante : ajoute une touche de miel pour une version sucrée-salée.' },
        { titre: 'Tzatziki fumé pimenté', description: 'Le tzatziki se réinvente : concombre croquant, yaourt grec épais, menthe fraîche… et cette note inattendue de Rouge Obsidienne qui vient chatouiller le palais. C\'est la fraîcheur qui pique, le contraste qui intrigue. À servir glacé avec brochettes ou falafels.' },
        { titre: 'Crème d\'aubergine grillée au KAZA', description: 'Une baba ganoush boostée : aubergines rôties au four, mixées avec tahini, ail, citron… et Rouge Obsidienne qui s\'infiltre dans chaque fibre fumée. La texture est veloutée, le goût est profond. À servir en couronne avec huile d\'olive et graines de sésame.' },
        { titre: 'Dip de lentilles corail & lait de coco', description: 'Une base douce et presque sucrée de lentilles corail cuites au lait de coco, relevée d\'un twist inattendu : Rouge Obsidienne. Le feu et la rondeur se marient ici dans un dip nourrissant, à tartiner ou à manger à la cuillère. Un vrai plat en soi.' },
        { titre: 'Caviar de betterave & fromage frais', description: 'Une purée de betterave d\'un rose éclatant, mêlée à du fromage frais fouetté. Rouge Obsidienne y ajoute une dimension mystérieuse, terreuse et fumée. Un dip chic, à dresser avec soin sur des assiettes de mezze ou dans des verrines.' },
        { titre: 'Guacamole pimenté fumé', description: 'Avocat écrasé, citron vert, coriandre fraîche… et une touche inattendue de Rouge Obsidienne qui vient fumer l\'ensemble. C\'est le Mexique qui flambe : douceur crémeuse, acidité vive, feu discret mais persistant. Un guacamole de caractère.' }
      ]},
      { nom: 'ENTRÉES', recettes: [
        { titre: 'Œuf parfait, crème de maïs fumée & Rouge Obsidienne', description: 'On laisse doucement cuire l\'œuf à basse température jusqu\'à ce qu\'il devienne soyeux, puis on le dépose sur une crème de maïs mixée et légèrement fumée. Une pointe de Rouge Obsidienne vient simplement relever l\'ensemble juste avant de servir.' },
        { titre: 'Velouté de potimarron & piment fumé', description: 'Le potimarron est cuit longuement pour libérer sa douceur, puis mixé en un velouté lisse et chaud. Le piment fumé est ajouté en fin de préparation pour apporter une chaleur maîtrisée.' },
        { titre: 'Tartare de betterave au chèvre & KAZA', description: 'La betterave coupée finement est mêlée à un chèvre frais détendu, puis assaisonnée de quelques gouttes de citron et d\'une touche de KAZA. L\'ensemble se dresse simplement, frais et coloré.' },
        { titre: 'Tarte fine tomate confite & fromage', description: 'On cuit une base feuilletée croustillante avant de la garnir de tomates confites et d\'un fromage fondant. Un filet d\'huile parfumée au piment vient réchauffer subtilement la dernière bouchée.' },
        { titre: 'Rouleaux de printemps à l\'avocat & KAZA', description: 'Les légumes croquants sont enveloppés avec l\'avocat en lamelles dans une feuille de riz souple. Un soupçon de KAZA glissé dans la garniture apporte l\'énergie et la fraîcheur nécessaire.' },
        { titre: 'Choux farcis façon street food', description: 'Les feuilles de chou sont attendries, puis enveloppent une farce généreuse parfumée de notes fumées et piquantes. Une légère cuisson au four ou à la poêle finalise le crousti-moelleux.' },
        { titre: 'Ceviche de crevettes & Rouge Obsidienne', description: 'Les crevettes sont marinées quelques minutes dans le citron vert avec oignon rouge et herbes fraîches. Rouge Obsidienne vient électriser la marinade juste au moment de l\'envoi.' },
        { titre: 'Œufs mimosa twistés (herbes & KAZA, chips de jambon)', description: 'On mélange les jaunes avec herbes fines et une pointe de KAZA pour obtenir une farce vivante et parfumée. Le tout est dressé dans les blancs puis surmonté d\'un éclat croustillant de jambon.' },
        { titre: 'Mini tartelettes carotte–cumin–KAZA', description: 'La carotte cuite doucement est réduite en purée sucrée et relevée d\'une pointe de cumin. Un voile de KAZA en finition transforme la douceur en vraie petite bouchée piquante.' },
        { titre: 'Carpaccio de champignons & noisettes, huile & KAZA', description: 'Les champignons sont tranchés finement et nappés d\'une huile douce, puis saupoudrés de noisettes concassées. Une touche de KAZA vient réveiller ce carpaccio tout en finesse.' }
      ]},
      { nom: 'PLATS MIJOTÉS', recettes: [
        { titre: 'Chili sin carne fumé', description: 'On fait mijoter lentilles, haricots et légumes dans une base tomate relevée, puis on ajoute une pointe fumée pour lui donner un caractère profond. La cuisson lente permet aux saveurs de se fondre jusqu\'à devenir un ragoût riche et sombre.' },
        { titre: 'Poulet mijoté coco–tomate–KAZA', description: 'Les morceaux de poulet dorés s\'enveloppent ensuite d\'une sauce lait de coco–tomate, douce et parfumée. Une pincée de KAZA réveille l\'ensemble et crée cette rondeur épicée qui devient vite addictive.' },
        { titre: 'Ragù épicé à la Rouge Obsidienne', description: 'Une base de viande hachée mijote longuement avec tomates, aromates et légumes. Rouge Obsidienne vient amplifier la bolognaise en lui donnant une profondeur légèrement fumée et piquante.' },
        { titre: 'Tajine de légumes racines', description: 'Les légumes sont saisis puis cuits doucement avec épices chaudes, fruits secs et bouillon. Le résultat : un équilibre entre douceur caramélisée, parfum d\'épices et chaleur subtile.' },
        { titre: 'Bœuf carottes revisité', description: 'On fait revenir les morceaux de bœuf, on ajoute carottes, oignons et un fond bien réduit. Le twist vient des épices fumées ou chaudes qui se mêlent au jus classique pour un plat à la fois familier et surprenant.' },
        { titre: 'Porc mijoté à la bière et piment', description: 'Les morceaux de porc marinent puis cuisent longuement dans la bière avec oignon et piment. La réduction crée un jus sucré-salé fumé qui enrobe la viande devenue fondante.' },
        { titre: 'Haricots blancs à la tomate confite', description: 'Les haricots sont mijotés avec ail, laurier et tomates confites jusqu\'à devenir crémeux. Une touche de Rouge Obsidienne apporte la profondeur épicée juste avant de servir.' },
        { titre: 'Agneau confit au miel et KAZA', description: 'L\'agneau cuit lentement avec un filet de miel, des épices et un soupçon de KAZA. La viande se détache d\'elle-même, nappée d\'un jus sucré-chaud irrésistible.' },
        { titre: 'Curry de légumes d\'hiver', description: 'Les légumes racines sont rôtis ou saisis puis plongés dans une sauce curry crémeuse. La puissance aromatique se développe doucement au fil de la cuisson, entre chaleur, rondeur et légères notes fumées si souhaité.' },
        { titre: 'Ratatouille fumée', description: 'Les légumes cuisent séparément pour garder leur caractère, puis mijotent ensemble avec un assaisonnement fumé. Le mélange épicé apporte un relief inattendu à la version classique.' },
        { titre: 'Dahl rouge pimenté', description: 'Les lentilles corail fondent dans le lait de coco et les épices, puis une pointe de KAZA vient relever l\'ensemble. On obtient un dahl velouté, chaud et généreux.' },
        { titre: 'Encornets farcis sauce tomate pimentée', description: 'Les encornets sont garnis d\'une farce parfumée puis mijotés doucement dans une sauce tomate relevée. Le piment donne juste assez de tension pour équilibrer la douceur de la cuisson.' },
        { titre: 'Poitrine de porc caramélisée au KAZA', description: 'La poitrine marine, cuit longuement, puis termine en caramélisation. KAZA apporte le contraste épicé qui fait croustiller le sucré.' },
        { titre: 'Paleron effiloché au vin rouge & KAZA', description: 'On laisse le paleron cuire longuement dans un mélange vin rouge–aromates avant de l\'effilocher. KAZA renforce le caractère du jus et donne une dimension plus sauvage au plat.' },
        { titre: 'Tendrons de veau au Rouge Obsidienne & KAZA', description: 'Les tendrons mijotent doucement jusqu\'à devenir fondants, baignés dans un jus enrichi d\'aromates et d\'épices. Le duo Rouge Obsidienne–KAZA apporte une profondeur épicée et une complexité chaleureuse.' }
      ]},
      { nom: 'VIANDES', recettes: [
        { titre: 'Côtelettes d\'agneau grillées au KAZA', description: 'Les côtelettes sont frottées généreusement de KAZA avant d\'être saisies à feu vif pour créer une croûte parfumée. On termine avec un yaourt citronné pour calmer la chaleur et prolonger les arômes.' },
        { titre: 'Filet mignon rôti au KAZA', description: 'Le filet est massé avec un mélange d\'huile et de KAZA avant de rôtir lentement pour s\'imprégner des épices. À la découpe, la viande reste juteuse avec une croûte épicée qui tient toutes les promesses.' },
        { titre: 'Brochettes de poulet fumées', description: 'Les morceaux de poulet reposent dans un yaourt parfumé au citron et à KAZA, rendant la chair tendre et parfumée. La cuisson barbecue apporte le fumé qui complète parfaitement le mélange.' },
        { titre: 'Tartare de bœuf twisté', description: 'La viande est assaisonnée comme un tartare classique, mais une pincée de KAZA y glisse une note chaude et légèrement fumée. Juste assez pour surprendre sans masquer le bœuf.' },
        { titre: 'Ribs caramélisés au KAZA', description: 'On masse les ribs de KAZA avant la cuisson lente, puis on les laque d\'un mélange miel–vinaigre. L\'épice se mêle au caramel pour donner une croûte collante et relevée.' },
        { titre: 'Magret de canard laqué au KAZA', description: 'Le magret est badigeonné d\'un mélange sucré-salé et de KAZA avant de rôtir. La peau devient brillante et parfumée, entre fumé, douceur et chaleur.' },
        { titre: 'Saucisses maison aux épices', description: 'La chair hachée est assaisonnée d\'ail, de sel et d\'une dose de KAZA avant d\'être embossée. À la cuisson, le mélange épicé diffuse dans toute la saucisse.' },
        { titre: 'Poulet croustillant façon wings', description: 'La panure est parfumée de KAZA avant friture, donnant un croustillant relevé. Une trempette fraîche vient équilibrer la montée en chaleur.' },
        { titre: 'Travers de porc grillés', description: 'Les travers marinent toute une nuit avec huile, ail et KAZA pour absorber toutes les épices. À la grillade, la viande prend un goût fumé relevé très net.' },
        { titre: 'Tajine de keftas fumées', description: 'Les boulettes sont roulées avec une pincée de KAZA, puis mijotées dans une sauce tomate épaisse. Le mélange donne une chaleur douce et persistante.' },
        { titre: 'Boulettes de viande au four', description: 'La farce est parfumée de KAZA et d\'herbes avant d\'être façonnée et cuite. Résultat : des boulettes juteuses, parfaites pour pita ou riz parfumé.' },
        { titre: 'Porc effiloché fumé', description: 'Le porc est frotté de KAZA puis cuit longuement jusqu\'à s\'effilocher tout seul. Le fumé et le piquant s\'intensifient dans le jus réduit.' },
        { titre: 'Burger d\'agneau au KAZA', description: 'La viande hachée est mélangée avec une petite dose de KAZA, juste assez pour relever sans masquer l\'agneau. Entre pain moelleux, oignons pickles et fromage, c\'est un équilibre explosif.' },
        { titre: 'Viande séchée maison', description: 'La viande est frottée d\'un rub sec au KAZA avant d\'être mise à sécher. Quelques jours plus tard, elle garde un parfum épicé profond et parfaitement intégré.' },
        { titre: 'Hachis parmentier twisté', description: 'La base de viande est relevée de KAZA avant d\'être couverte de purée crémeuse. À la cuisson, les épices infusent et transforment entièrement ce classique.' }
      ]},
      { nom: 'POISSONS & FRUITS DE MER', recettes: [
        { titre: 'Filet de poisson laqué au KAZA', description: 'On badigeonne le poisson d\'un mélange miel–citron–huile et KAZA, puis on enfourne jusqu\'à obtenir un laquage brillant et parfumé. La cuisson courte garde la chair fondante tandis que les épices caramélisent en surface.' },
        { titre: 'Brochettes de crevettes épicées', description: 'Les crevettes reposent quelques minutes dans huile, ail et KAZA avant d\'être grillées rapidement. Le mélange s\'accroche à la carapace, offrant un résultat simple mais explosif.' },
        { titre: 'Tartare de thon rouge & KAZA', description: 'Le thon coupé au couteau est mêlé à citron, huile et un voile de KAZA pour une chaleur discrète et fumée. Juste assez pour donner du relief sans dominer le cru.' },
        { titre: 'Ceviche de daurade twisté', description: 'Les cubes de daurade marinent brièvement dans agrumes, coriandre et sel, puis une pointe de KAZA apporte le punch final. On mélange au dernier moment pour garder la fraîcheur intacte.' },
        { titre: 'Papillote de saumon fumée', description: 'On pose saumon, courgettes et tomates cerise dans une papillote, arrosés d\'huile et assaisonnés d\'un soupçon de KAZA. À la vapeur du four, les légumes confisent doucement et l\'épice diffuse un fumé délicat.' },
        { titre: 'Moules à la crème et au KAZA', description: 'Les moules s\'ouvrent dans vin blanc et échalote, puis on ajoute une cuillère de crème mêlée à KAZA. Le résultat : une sauce douce avec une chaleur inattendue mais harmonieuse.' },
        { titre: 'Poisson entier rôti en croûte d\'épices', description: 'Le poisson est massé avec huile, sel et une généreuse couche de KAZA avant d\'être enfourné. La croûte chauffe, crépite légèrement et parfume toute la chair.' },
        { titre: 'Brandade revisitée', description: 'On mélange poisson poché, pomme de terre et huile d\'olive, puis on ajoute une petite dose de KAZA pour donner tension et nervosité. Juste assez pour réveiller la douceur traditionnelle.' },
        { titre: 'Gambas flambées au mezcal & KAZA', description: 'Les gambas sont saisies à feu fort avec ail et KAZA avant d\'être flambées au mezcal. L\'alcool s\'évapore, ne laissant qu\'un parfum fumé et vibrant.' },
        { titre: 'Burger de poisson au KAZA', description: 'Le poisson haché ou en filet est assaisonné de KAZA avant cuisson, créant une base juteuse et relevée. Entre pain moelleux, sauce fraîche et pickles, le contraste est parfait.' },
        { titre: 'Risotto de la mer fumé', description: 'Les fruits de mer sont saisis séparément, puis ajoutés au risotto en fin de cuisson avec une pincée de KAZA. L\'épice se fond dans le crémeux, apportant un fumé subtil.' },
        { titre: 'Calamars sautés minute (Rouge Obsidienne)', description: 'Les anneaux de calamars sont jetés dans une poêle très chaude avec citron, huile et une pincée de Rouge Obsidienne. La cuisson ultra rapide garde le moelleux et fixe la note épicée.' },
        { titre: 'Poisson pané maison', description: 'On ajoute une cuillère de KAZA à la chapelure avant de paner les filets. À la cuisson, la croûte devient dorée, croustillante et parfaitement parfumée.' },
        { titre: 'Curry de poisson express', description: 'Le poisson est poché directement dans lait de coco, citron vert et une pointe de KAZA. La cuisson courte évite de casser la chair et laisse une sauce vibrante.' },
        { titre: 'Sashimi sauce soja–KAZA', description: 'Quelques grains de KAZA sont mélangés à la sauce soja, juste assez pour la réchauffer. On en dépose un filet sur chaque tranche de poisson pour un topping discret mais indispensable.' }
      ]},
      { nom: 'STREET FOODS', recettes: [
        { titre: 'Smash burger au KAZA', description: 'La viande est légèrement mélangée à une pincée de KAZA avant d\'être écrasée sur la plaque, créant une croûte parfumée. Le résultat : un burger juteux, relevé juste ce qu\'il faut.' },
        { titre: 'Hot-dog pimenté–fumé', description: 'La saucisse est grillée puis roulée dans un voile de KAZA pour lui donner ce côté fumé-piquant. On la glisse dans un pain moelleux avec pickles et moutarde douce pour l\'équilibre.' },
        { titre: 'Quesadillas from hell', description: 'Le fromage fond avec oignons et une touche de KAZA avant d\'être enfermé dans la tortilla grillée. La chaleur de l\'épice se mêle au lacté pour un effet « hell yes ».' },
        { titre: 'Tacos de bœuf fumé', description: 'Le bœuf mijoté ou effiloché est assaisonné de KAZA pour apporter la note fumée qui fait toute la différence. On termine avec herbes fraîches et citron vert pour la fraîcheur.' },
        { titre: 'Wrap poulet–coco–KAZA', description: 'Le poulet cuit dans une petite sauce coco parfumée, puis relevée d\'une pointe de KAZA. Le tout est roulé dans une tortilla pour un wrap crémeux et piquant.' },
        { titre: 'Empanadas épicées', description: 'La garniture — viande ou légumes — est relevée d\'une cuillère de KAZA avant d\'être enfermée dans la pâte. À la cuisson, l\'épice chauffe et réveille chaque bouchée.' },
        { titre: 'Bao buns effiloché de porc', description: 'Le porc effiloché est mélangé avec une sauce légèrement sucrée et une touche de KAZA. Dans un bao bien chaud, ça devient un nuage parfumé.' },
        { titre: 'Sandwich pané veggie & KAZA', description: 'L\'aubergine ou le tofu sont panés dans une chapelure parfumée au KAZA, créant un croustillant épicé. On assemble avec sauce fraîche et crudités.' },
        { titre: 'Frites de patate douce & mayo KAZA', description: 'Les frites rôties sont servies avec une mayonnaise montée avec un trait de KAZA. Sucré, croustillant, relevé : combo imparable.' },
        { titre: 'Grilled cheese twisté', description: 'Le fromage fond entre deux tranches beurrées, agrémenté d\'une pincée de KAZA au milieu. Une fois grillé, le sandwich révèle un cœur chaud et épicé.' },
        { titre: 'Banh mi fusion', description: 'La viande ou le tofu est parfumé de KAZA avant montage, apportant une chaleur fumée à ce classique vietnamien. Pickles, coriandre et mayo complètent le twist.' },
        { titre: 'Pizza blanche au KAZA', description: 'La base crème-fromage est relevée de KAZA avant cuisson, ce qui parfume toute la surface à la chaleur du four. Une pizza blanche pleine de caractère.' },
        { titre: 'Croque-monsieur revisité', description: 'Entre jambon fumé et fromage coulant, une pointe de KAZA est glissée pour donner un relief épicé. Le tout gratine et dévoile une profondeur inattendue.' },
        { titre: 'Kebab maison épicé', description: 'La viande est marinée dans huile, citron et KAZA, puis grillée avant d\'être enfermée dans un pain pita chaud. On ajoute une sauce blanche citronnée pour tempérer.' },
        { titre: 'Falafels fumés', description: 'La pâte à falafels intègre une petite dose de KAZA, donnant une chaleur fumée au cœur. On sert le tout avec un yaourt citron vert tout simple.' }
      ]},
      { nom: 'DESSERTS', recettes: [
        { titre: 'Brownie au piment fumé', description: 'On glisse une pincée de KAZA dans la pâte au chocolat encore tiède, pour qu\'il se fonde parfaitement. À la cuisson, le brownie reste fondant avec une chaleur douce qui arrive en fin de bouche.' },
        { titre: 'Poêlée de fruits au KAZA', description: 'Mangue, ananas et banane caramélisent dans un peu de beurre et de sucre, puis reçoivent une touche légère de KAZA hors du feu. L\'épice réchauffe les fruits sans les dominer.' },
        { titre: 'Compote pommes–coings & Rouge Obsidienne', description: 'Les fruits mijotent doucement jusqu\'à fondre, puis on ajoute une pointe de Rouge Obsidienne pour un twist chaud et inattendu. La compote reste douce mais gagne un caractère surprenant.' },
        { titre: 'Mousse au chocolat noir twistée', description: 'Une toute petite dose de KAZA est incorporée au chocolat fondu avant de le mélanger aux blancs. À la dégustation, le fumé apparaît subtilement derrière l\'amertume du chocolat.' },
        { titre: 'Glace vanille–KAZA', description: 'On mélange la vanille à une pointe de KAZA dans la base glacée avant turbinage. Le froid fait ressortir l\'épice différemment, créant un contraste proprement addictif.' },
        { titre: 'Tarte fine poire–piment', description: 'Les fines lamelles de poire sont saupoudrées d\'un voile de KAZA avant cuisson, ce qui caramélise avec le sucre naturel du fruit. Le résultat : une tarte juteuse, sucrée, légèrement piquante.' },
        { titre: 'Cookies chocolat noir–KAZA', description: 'Quelques grains de KAZA sont ajoutés à la pâte juste avant d\'incorporer le chocolat. À la cuisson, l\'épice se fond dans la mie moelleuse pour créer un cookie chaud et addictif.' },
        { titre: 'Crème brûlée au KAZA', description: 'La crème est infusée à très faible dose de KAZA avant d\'être cuite au four. Sous la croûte caramélisée, on découvre un parfum épicé discret mais bluffant.' },
        { titre: 'Gâteau marbré chocolat–épices', description: 'On ajoute une pincée de KAZA dans la partie claire de la pâte, créant des rubans moelleux parfumés qui contrastent avec le chocolat. Un marbré simple mais plein de personnalité.' },
        { titre: 'Pain perdu caramélisé au KAZA', description: 'Le pain trempé est poêlé dans du beurre et du sucre jusqu\'à caramélisation, puis saupoudré d\'une touche de KAZA encore chaud pour fixer l\'arôme. Un dessert comfort food à explosion douce.' }
      ]}
    ]
  },
      'eveil-floral': {
      couleur: '#728CC0',
      titre: 'Éveil Floral',
      categories: [
        { nom: 'DIPS', recettes: [
          { titre: 'Houmous aux herbes fraîches', description: 'Les pois chiches mixés avec tahini et citron reçoivent une belle pincée d\'Éveil Floral en fin de préparation, ce qui apporte une fraîcheur immédiate. Le mélange s\'exprime surtout à froid : parfait pour un houmous léger et végétal.' },
          { titre: 'Crème de feta fouettée aux herbes', description: 'La feta est montée avec un peu de yaourt, puis parfumée d\'Éveil Floral pour lui donner une aromatique vive. Le résultat est mousseux, salin et délicatement herbacé.' },
          { titre: 'Tartinade de haricots blancs & citron', description: 'Les haricots sont mixés avec ail doux, huile d\'olive et citron, puis réveillés d\'un trait d\'Éveil Floral. L\'épice apporte une fraîcheur qui tranche avec le côté crémeux du haricot.' },
          { titre: 'Rillettes de sardines & citron à l\'Éveil Floral', description: 'Les sardines écrasées avec huile, citron et fromage frais gagnent en relief grâce à une pincée d\'Éveil Floral. Cela ajoute des notes végétales qui tempèrent l\'iode et éclaircissent la tartinade.' },
          { titre: 'Beurre aux herbes fraîches', description: 'Le beurre mou est travaillé avec herbes ciselées et Éveil Floral, puis mis au frais pour se structurer. Sur pain chaud ou légumes rôtis, il fond en libérant un parfum vert immédiat.' },
          { titre: 'Tzatziki aux herbes fraîches', description: 'Concombre râpé et yaourt grec sont mélangés avec menthe, citron et une touche d\'Éveil Floral. Le mélange intensifie la sensation de fraîcheur et prolonge le parfum des herbes.' },
          { titre: 'Crème d\'aubergine grillée à l\'Éveil Floral', description: 'L\'aubergine rôtie est mixée avec tahini et citron, puis parfumée d\'Éveil Floral pour ajouter une dimension florale et végétale. Le contraste entre fumé de l\'aubergine et fraîcheur de l\'épice fonctionne à merveille.' },
          { titre: 'Dip de lentilles corail & lait de coco', description: 'Les lentilles cuites dans le lait de coco deviennent douces et épaisses ; une pincée d\'Éveil Floral en finition apporte la fraîcheur qui leur manquait. L\'épice équilibre la rondeur du coco.' },
          { titre: 'Caviar de betterave & fromage frais', description: 'Betterave mixée, fromage frais et un filet de citron sont relevés par Éveil Floral, qui ajoute des notes herbacées et florales. Le résultat est rose, brillant et étonnamment frais.' },
          { titre: 'Guacamole aux herbes fraîches', description: 'L\'avocat écrasé avec citron vert et coriandre reçoit une touche d\'Éveil Floral juste avant de servir. Le mélange accentue les notes végétales et donne un guacamole plus clair, plus vif.' }
        ]},
        { nom: 'ENTRÉES', recettes: [
          { titre: 'Tartare de pêche & burrata', description: 'Les dés de pêche sont mêlés à un filet d\'huile d\'olive et de citron, puis une pincée d\'Éveil Floral vient relever leur fraîcheur naturelle. La burrata posée dessus absorbe les parfums et apporte la douceur crémeuse.' },
          { titre: 'Œuf croustillant & petits pois', description: 'L\'œuf frit au cœur coulant est déposé sur une purée ou des petits pois entiers encore croquants. Une touche d\'Éveil Floral donne un éclat végétal qui contraste avec le croustillant.' },
          { titre: 'Gravlax de truite au gin', description: 'La truite repose dans un mélange sel–sucre–gin agrémenté d\'Éveil Floral, qui diffuse ses notes florales et herbacées pendant la marinade. Le résultat est parfumé à froid, délicat et parfaitement équilibré.' },
          { titre: 'Chèvre chaud au miel & seigle', description: 'Le chèvre légèrement fondu sur un toast de seigle reçoit un filet de miel, puis une pincée d\'Éveil Floral juste avant de servir. L\'épice apporte une dimension végétale qui allège la richesse du fromage.' },
          { titre: 'Encornets aux herbes fraîches', description: 'Les encornets saisis très rapidement sont ensuite mélangés avec citron, huile et une pointe d\'Éveil Floral. Cela crée une assiette légère, brillante et parfumée d\'herbes.' },
          { titre: 'Ceviche de crevettes & Éveil Floral', description: 'Les crevettes marinées dans agrumes et coriandre gagnent en fraîcheur grâce à une pincée d\'Éveil Floral ajoutée au dernier moment. Le mélange amplifie les notes citronnées et florales du ceviche.' },
          { titre: 'Œufs mimosa twistés', description: 'Les jaunes écrasés avec herbes, mayo et citron intègrent une petite dose d\'Éveil Floral pour une aromatique nette. Dans les blancs, la garniture devient fraîche, claire et légère.' },
          { titre: 'Carpaccio de champignons & noisettes', description: 'Les fines lamelles de champignons sont arrosées d\'huile douce, parsemées de noisettes torréfiées, puis relevées d\'Éveil Floral. L\'épice fait ressortir leur parfum boisé tout en ajoutant une note végétale.' },
          { titre: 'Rouleaux de printemps à l\'avocat', description: 'Légumes croquants, vermicelles et avocat sont roulés serrés, puis assaisonnés d\'un trait de citron et d\'Éveil Floral avant de fermer la feuille de riz. La bouchée est fraîche, vive et élégante.' },
          { titre: 'Tarte fine tomate confite & fromage', description: 'Sur la pâte croustillante, les tomates confites et le fromage tiède reçoivent une pincée d\'Éveil Floral à la sortie du four. La fraîcheur herbacée équilibre le côté sucré et riche.' }
        ]},
        { nom: 'PLATS MIJOTÉS', recettes: [
          { titre: 'Poulet coco–tomate parfumé', description: 'Le poulet mijote dans une sauce douce au lait de coco et tomate, puis une pincée d\'Éveil Floral est ajoutée en fin de cuisson pour apporter fraîcheur d\'agrumes et parfum d\'herbes.' },
          { titre: 'Ragù blanc aux notes citronnées', description: 'Une base de viande mijotée au vin blanc et aux herbes, relevée au dernier moment d\'Éveil Floral, qui apporte une clarté aromatique et une touche florale qui modernise le plat.' },
          { titre: 'Tajine léger de légumes racines', description: 'Les légumes cuisent doucement avec citron et épices douces, puis Éveil Floral est ajouté hors du feu pour illuminer la douceur des racines d\'une fraîcheur herbacée.' },
          { titre: 'Veau carottes au parfum d\'herbes', description: 'La viande fond lentement dans un jus citronné, et l\'ajout d\'Éveil Floral à la fin apporte une note florale vive qui met en valeur les carottes.' },
          { titre: 'Porc mijoté aux herbes fraîches', description: 'Après une cuisson lente dans bière blonde et aromates, une pincée d\'Éveil Floral réveille la sauce d\'une fraîcheur citronnée qui allège le plat.' },
          { titre: 'Haricots blancs à la tomate confite', description: 'Les haricots mijotent avec ail, laurier et tomate confite ; Éveil Floral, ajouté au moment du dressage, apporte une touche lumineuse entre herbes et agrumes.' },
          { titre: 'Agneau confit au miel parfumé', description: 'L\'agneau fond délicatement après une longue cuisson, et quelques grains d\'Éveil Floral en sortie équilibrent le miel par une légère fraîcheur florale.' },
          { titre: 'Curry vert de légumes', description: 'Les légumes s\'imprègnent d\'une sauce herbacée et citronnée, et Éveil Floral vient accentuer la vivacité du curry grâce à ses notes fraîches et florales.' },
          { titre: 'Ratatouille moderne aux herbes', description: 'Les légumes, cuits séparément puis réunis, gardent leur netteté. Une pincée d\'Éveil Floral en finition apporte une fraîcheur aromatique qui renouvelle ce classique.' },
          { titre: 'Dahl citronné & herbes', description: 'Les lentilles corail mijotent avec citron et épices douces, puis Éveil Floral ajouté hors du feu apporte une touche végétale et florale qui équilibre parfaitement la douceur du dahl.' }
        ]},
        { nom: 'VIANDES BLANCHES', recettes: [
          { titre: 'Filet mignon vapeur douce & herbes citronnées', description: 'Le filet mignon cuit doucement à la vapeur pour rester rosé et juteux, puis est nappé d\'un jus clair au citron et au bouillon blanc. Une pincée d\'Éveil Floral en finition apporte une fraîcheur florale délicate.' },
          { titre: 'Poulet grillé au yaourt et herbes fraîches', description: 'Le poulet marine dans yaourt, citron et herbes, puis dore au grill. En sortie, Éveil Floral renforce le côté végétal et fait vibrer la marinade.' },
          { titre: 'Dinde rôtie au pamplemousse rose & poivre blanc', description: 'La volaille cuit lentement avec des quartiers d\'agrumes, révélant une chair parfumée et juteuse. Éveil Floral ajouté juste avant de servir amplifie l\'éclat citronné du pamplemousse.' },
          { titre: 'Veau poêlé, jus court au vin blanc & fleurs d\'agrumes', description: 'Le veau est poêlé puis nappé d\'un jus réduit très clair. Quelques grains d\'Éveil Floral insufflent une dimension florale qui illumine le plat sans le dominer.' },
          { titre: 'Lapin confit au citron vert & estragon', description: 'Le lapin mijote dans une base légère, parfumée d\'estragon et de zestes. Éveil Floral déposé à froid rafraîchit la sauce et apporte une touche végétale subtile.' },
          { titre: 'Porc grillé aux pêches rôties & herbes vives', description: 'Des tranches de porc grillées sont accompagnées de pêches légèrement caramélisées. Un peu d\'Éveil Floral vient relier la douceur du fruit à la viande avec un twist agrumé.' },
          { titre: 'Poulet rôti gingembre–citronnelle', description: 'Le poulet rôtit avec un mélange gingembre, citronnelle et miel léger. À la découpe, une pointe d\'Éveil Floral ajoute une fraîcheur florale qui modernise totalement le plat.' },
          { titre: 'Dinde braisée au foin & touche florale', description: 'Une cuisson douce dans un foin parfumé qui donne une note rustique et douce. Éveil Floral vient équilibrer cette rusticité avec un parfum floral clair.' },
          { titre: 'Veau aux champignons blancs & crème citronnée', description: 'Les champignons cuisent dans une crème légère au citron ; le veau se mêle ensuite à la sauce. Éveil Floral accentue le côté frais et floral tout en restant délicat avec les champignons.' },
          { titre: 'Poulet caramélisé miel–citron & aromates', description: 'Un poulet doré puis laqué d\'un mélange miel–citron. Quelques grains d\'Éveil Floral au dernier moment apportent relief, fraîcheur et une dimension florale qui coupe la sucrosité.' }
        ]},
        { nom: 'POISSONS', recettes: [
          { titre: 'Dorade grillée aux herbes fraîches', description: 'La dorade, grillée peau bien croustillante, est arrosée d\'un jus citronné puis finie avec Éveil Floral, qui révèle les parfums méditerranéens sans alourdir la chair.' },
          { titre: 'Saumon en croûte d\'herbes parfumée', description: 'Une croûte croustillante aux herbes et au citron protège le saumon et garde son moelleux. Éveil Floral vient parfumer la croûte une fois sortie du four, apportant fraîcheur et floralité.' },
          { titre: 'Tartare de thon aux agrumes', description: 'Thon coupé au couteau, zestes et jus d\'agrumes, huile d\'olive : Éveil Floral apporte un parfum floral subtil qui se marie naturellement avec le cru.' },
          { titre: 'Bar rôti au fenouil & huile chaude', description: 'Le bar est rôti sur un lit de fenouil, puis arrosé d\'une huile chaude où Éveil Floral est ajouté juste avant le service : la chaleur réveille ses notes d\'agrumes et de fleur sans les cuire.' },
          { titre: 'Truite aux amandes torréfiées', description: 'La truite poêlée reçoit des amandes grillées et un filet de citron. Éveil Floral ajoute une dimension florale qui équilibre le croquant et la délicatesse du poisson.' },
          { titre: 'Sole meunière citronnée', description: 'Un beurre meunière très léger, enrichi d\'un zeste de citron, est parfumé en fin de cuisson avec une pincée d\'Éveil Floral. La sole devient raffinée, brillante, moderne.' },
          { titre: 'Thon mi-cuit sésame & fleurs', description: 'Le thon est snacké en croûte de sésame, puis surmonté d\'un mélange citronné et floral à base d\'Éveil Floral. Le contraste puissance/fraîcheur devient explosif.' },
          { titre: 'Cabillaud confit dans l\'huile d\'olive', description: 'Le cabillaud cuit lentement à basse température dans une huile douce. À froid, Éveil Floral est ajouté pour offrir une note fraîche qui contraste avec la cuisson fondante.' },
          { titre: 'Lotte rôtie aux zestes & herbes', description: 'Sa chair ferme supporte un jus clair au citron et vin blanc, parfumé en fin de cuisson d\'Éveil Floral. Les zestes accentuent les agrumes, le mélange apporte la touche florale.' },
          { titre: 'Rouget grillé façon Riviera', description: 'Le rouget, grillé rapidement, est servi avec une garniture minute de tomates concassées, herbes et un voile d\'Éveil Floral. L\'ensemble évoque la Méditerranée éclatante et fraîche.' }
        ]},
        { nom: 'SALADES', recettes: [
          { titre: 'Salade de lentilles tiède & oignons rouges', description: 'Les lentilles encore tièdes sont mélangées à une vinaigrette citronnée, puis Éveil Floral est ajouté pour apporter une fraîcheur florale qui contraste avec la rondeur de la lentille.' },
          { titre: 'Salade de quinoa, avocat & pamplemousse', description: 'Le quinoa refroidi absorbe un jus d\'agrumes vif, tandis que l\'avocat apporte le moelleux. Éveil Floral renforce ici les notes citronnées et donne un parfum lumineux au pamplemousse.' },
          { titre: 'Coleslaw aux herbes parfumé', description: 'Le chou finement émincé est lié à une sauce légère yaourt–citron. Une pincée d\'Éveil Floral dans la sauce apporte une aromatique florale qui modernise complètement le coleslaw.' },
          { titre: 'Salade de pois chiches rôtis croquante', description: 'Les pois chiches grillent au four pour devenir croustillants, puis sont mêlés à herbes fraîches et citron. Éveil Floral vient se déposer à froid pour un parfum herbacé qui sublime le croquant.' },
          { titre: 'Taboulé aux herbes fraîches', description: 'Le boulgour gonfle à froid puis s\'enrichit de menthe, persil, citron et tomates. Une touche d\'Éveil Floral amplifie la sensation de fraîcheur végétale et rend le taboulé plus vibrant.' },
          { titre: 'Salade de pommes de terre aux herbes vives', description: 'Les pommes de terre tièdes sont enrobées d\'une vinaigrette moutarde–citron, puis parsemées d\'oignons frits. Un soupçon d\'Éveil Floral ajoute une note florale qui allège le tout.' },
          { titre: 'Salade grecque revisitée', description: 'Feta, tomates, olives et concombre sont mélangés avec une huile d\'olive citronnée. Éveil Floral apporte une fraîcheur herbacée qui illumine le sel de la feta et la douceur de la tomate.' },
          { titre: 'Salade de riz noir & légumes croquants', description: 'Le riz noir, dense et parfumé, rencontre radis, concombre, herbes et un filet de citron. Éveil Floral relève l\'ensemble d\'une note agrumée qui accentue le contraste textures/couleurs.' },
          { titre: 'Carottes râpées & vinaigrette citron–floral', description: 'Les carottes râpées sont arrosées d\'une vinaigrette légère au miel et au citron. Une pincée d\'Éveil Floral apporte une fraîcheur florale qui transforme ce classique en petite bombe aromatique.' },
          { titre: 'Salade d\'œufs mimosa revisitée', description: 'Les œufs durs sont coupés en quartiers et assaisonnés d\'une sauce citronnée aux herbes. Éveil Floral glissé dans la sauce donne une finesse florale qui modernise totalement l\'esprit "mimosa".' }
        ]},
        { nom: 'DESSERTS', recettes: [
          { titre: 'Crème citron & pêche au sarrasin', description: 'Une crème douce au citron rencontre des pêches légèrement rôties ; une pointe d\'Éveil Floral ajoutée à froid apporte un parfum floral qui prolonge la fraîcheur acidulée.' },
          { titre: 'Poêlée de fruits caramélisés & touche florale', description: 'Mangue, ananas et banane caramélisent au beurre, puis une pincée d\'Éveil Floral hors du feu apporte une note citronnée et végétale qui équilibre la douceur.' },
          { titre: 'Compote pommes–coings parfumée', description: 'Les fruits mijotent doucement jusqu\'à fondre, puis reçoivent Éveil Floral en finition pour ajouter une fraîcheur florale qui surprend sans dominer.' },
          { titre: 'Mousse au chocolat noir aux herbes fines', description: 'Le chocolat fondu intègre une micro-dose d\'Éveil Floral, juste assez pour glisser une note végétale subtile derrière l\'amertume du cacao.' },
          { titre: 'Glace vanille au parfum floral', description: 'La base vanille est parfumée d\'une pincée d\'Éveil Floral avant turbinage, créant un contraste froid–floral vibrant et très élégant.' },
          { titre: 'Tarte fine poire & herbes fraîches', description: 'Les poires tranchées finement caramélisent au four sur une pâte croustillante ; Éveil Floral déposé à la sortie ajoute une fraîcheur florale qui relève leur jus sucré.' },
          { titre: 'Cookies chocolat noir parfumés', description: 'Quelques grains d\'Éveil Floral sont incorporés dans la pâte avant cuisson, apportant une note citronnée et florale qui rend le chocolat encore plus addictif.' },
          { titre: 'Crème brûlée twistée', description: 'La crème infuse très légèrement avec Éveil Floral avant cuisson, et le caramel chaud de la surface réveille les notes florales en bouche. Un twist fin mais bluffant.' },
          { titre: 'Gâteau marbré chocolat & herbes', description: 'Une partie de l\'appareil est parfumée d\'Éveil Floral, créant un marbrage moelleux où le chocolat s\'équilibre avec une note fraîche et végétale.' },
          { titre: 'Pain perdu caramélisé parfumé', description: 'Le pain doré dans le beurre et le sucre reçoit une pincée d\'Éveil Floral encore chaud, ce qui apporte une fraîcheur florale qui coupe la gourmandise et rend chaque bouchée plus vive.' }
        ]}
      ]
    },
      'bois-fauve': {
      couleur: '#1A3713',
      titre: 'Bois Fauve',
      categories: [
        { nom: 'DIPS', recettes: [
          { titre: 'Houmous boisé au Bois Fauve', description: 'Les pois chiches mixés avec tahini et ail rôti se marient idéalement à Bois Fauve, qui apporte une profondeur aromatique boisée et herbacée. Le mélange donne un houmous structuré et chaleureux.' },
          { titre: 'Crème de carottes rôties au yaourt grec', description: 'Les carottes rôties avec Bois Fauve développent une douceur terrienne. Une fois mixées avec du yaourt grec, on obtient une crème équilibrée, ronde et subtilement aromatique.' },
          { titre: 'Dip d\'oignons caramélisés & fromage frais', description: 'Les oignons caramélisent lentement jusqu\'à devenir doux et bruns. Bois Fauve, ajouté pendant la cuisson, apporte une note boisée et aromatique qui renforce la profondeur des oignons. Le fromage frais apporte la légèreté finale.' },
          { titre: 'Caviar de courgette tiède aux herbes', description: 'Les courgettes grillées sont mixées avec ail, citron et un soupçon de Bois Fauve, qui leur donne un caractère végétal et structuré. Servi tiède, le caviar révèle toute sa dimension aromatique.' },
          { titre: 'Beurre de haricots blancs à l\'ail confit', description: 'Haricots blancs mixés avec ail confit et citron, puis relevés d\'une touche de Bois Fauve qui apporte rondeur boisée et profondeur herbacée. Texture ultra onctueuse, saveur élégante.' },
          { titre: 'Fromage fouetté aux herbes & Bois Fauve', description: 'Le fromage frais battu avec un peu de crème devient aérien, puis se parfume de ciboulette, fenouil frais et Bois Fauve. Une combinaison florale et forestière qui donne une élégance rare.' },
          { titre: 'Baba ganoush boisé', description: 'Les aubergines rôties gagnent en relief grâce à Bois Fauve, qui apporte une aromatique herbacée et boisée parfaitement cohérente avec le tahini. Le résultat est profond, terrien et harmonieux.' },
          { titre: 'Tartinade de champignons & noix', description: 'Les champignons poêlés avec échalote sont mixés avec des noix grillées, puis relevés de Bois Fauve pour renforcer leurs notes forestières naturelles. Un dip dense, élégant, très terroir.' },
          { titre: 'Rillettes végétales lentilles–oignons', description: 'Les lentilles vertes écrasées avec oignons frits et huile de noix deviennent une crème rustique. Bois Fauve apporte une structure aromatique boisée qui donne du relief au mélange.' },
          { titre: 'Tomates confites & mascarpone parfumé', description: 'Les tomates confites longuement absorbent Bois Fauve, qui leur apporte une note herbacée et boisée. Mélangées au mascarpone, elles créent une tartinade douce mais profonde.' }
        ]},
        { nom: 'ENTRÉES', recettes: [
          { titre: 'Œufs mollets sur velouté de panais', description: 'Le panais mixé avec crème et un trait de vinaigre de cidre reçoit une pointe de Bois Fauve, qui apporte une profondeur boisée subtile. L\'œuf mollet se mêle à la douceur du velouté.' },
          { titre: 'Champignons farcis au fromage frais & noix', description: 'Les têtes de champignons sont garnies d\'un mélange fromage frais–noix–échalote. Un soupçon de Bois Fauve dans la farce renforce le caractère forestier de l\'ensemble.' },
          { titre: 'Tarte fine à l\'oignon confit & chèvre', description: 'Une pâte dorée accueille des oignons longuement compotés avec Bois Fauve, puis du chèvre frais. Le mélange aromatique structure la douceur des oignons.' },
          { titre: 'Rouleaux de chou vert vapeur, farce végétale', description: 'Une farce lentilles–oignons–herbes parfumée à Bois Fauve est roulée dans des feuilles de chou vapeur. Une entrée légère mais très aromatique.' },
          { titre: 'Galette de polenta grillée, crème de champignons', description: 'La polenta grillée devient croustillante, puis s\'accompagne d\'une sauce champignons–crème infusée d\'une pincée de Bois Fauve, pour une aromatique chaleureuse.' },
          { titre: 'Millefeuille de tomates confites & ricotta', description: 'Les tomates confites au four avec huile et Bois Fauve sont superposées à de la ricotta citronnée et du basilic. Une bouchée douce, boisée et lumineuse.' },
          { titre: 'Crème d\'œufs brouillés aux champignons', description: 'Des œufs brouillés ultra crémeux accueillent des champignons poêlés et une pointe de Bois Fauve qui leur donne de la profondeur sans couvrir le beurre ni les œufs.' },
          { titre: 'Croquettes de pommes de terre aux herbes', description: 'Pommes de terre râpées, herbes fraîches et oignons frits se mêlent à une petite dose de Bois Fauve avant friture, créant une croquette à la fois rustique et parfumée.' },
          { titre: 'Poires pochées salées, fromage & noix', description: 'Les poires sont pochées dans un bouillon clair parfumé de Bois Fauve, puis farcies d\'un mélange bleu–noix. L\'aromatique boisée s\'allie parfaitement au fruit.' },
          { titre: 'Pain perdu salé au comté & fenouil', description: 'Le pain trempé dans un appareil œuf–crème parfumé d\'une pointe de Bois Fauve cuit jusqu\'à devenir doré. Servi avec du comté râpé et un peu de fenouil frais.' }
        ]},
        { nom: 'PLATS MIJOTÉS', recettes: [
          { titre: 'Ragoût de légumes racines & lentilles corail', description: 'Les légumes rôtis puis mijotés avec lentilles corail forment une base douce et terrienne. Une pincée de Bois Fauve apporte une profondeur herbacée qui structure le lait de coco et équilibre la douceur des racines.' },
          { titre: 'Tajine de poulet au fenouil & olives vertes', description: 'Le poulet mijote avec fenouil, citron confit et olives, créant un bouillon clair et aromatique. Bois Fauve ajouté en fin de cuisson renforce l\'aromatique méditerranéenne avec une note boisée subtile.' },
          { titre: 'Daube végétarienne de pois chiches', description: 'Pois chiches, tomates, carottes et dattes cuisent lentement jusqu\'à former une sauce riche et ronde. Bois Fauve complète la douceur par une profondeur herbacée et terrienne.' },
          { titre: 'Navarin de veau forestier', description: 'Le veau mijote dans un jus au vin blanc avec carottes, navets et champignons. Bois Fauve met en valeur le côté forestier du plat en apportant une note boisée naturelle.' },
          { titre: 'Curry doux de courge & pois chiches', description: 'La courge fond dans une sauce coco–tomate légère, tandis que Bois Fauve vient donner un relief aromatique, évitant que le plat ne soit trop sucré. Un curry doux, terrien, parfaitement équilibré.' },
          { titre: 'Sauté de dinde au chou & cumin', description: 'La dinde poêlée avec chou blanc et oignons crée un plat simple et réconfortant. Une pointe de Bois Fauve ajoute une note boisée qui dialogue bien avec le cumin.' },
          { titre: 'Haricots blancs à la tomate & oignons frits', description: 'Les haricots mijotent dans une sauce tomate douce et concentrée. Bois Fauve amène une aromatique boisée qui structure la sauce et rend les oignons frits encore plus parfumés.' },
          { titre: 'Saucisses–lentilles au vin rouge', description: 'Les lentilles cuisent avec vin rouge, saucisses et aromates. Bois Fauve renforce le caractère terrien du plat et s\'accorde parfaitement avec la profondeur du vin.' },
          { titre: 'Pot-au-feu revisité aux herbes boisées', description: 'Un bouillon clair infusé à Bois Fauve accompagne jarret et légumes anciens. Le mélange apporte un parfum herbacé sec, qui modernise ce grand classique sans le dénaturer.' },
          { titre: 'Ragoût de champignons sauvages & pommes de terre', description: 'Les champignons et pommes de terre mijotent dans une crème légère. Bois Fauve intensifie les notes forestières et ajoute une élégance herbacée parfaitement naturelle.' }
        ]},
        { nom: 'VIANDES', recettes: [
          { titre: 'Échine de porc rôtie, croûte boisée', description: 'La marinade sèche — moutarde, ail et Bois Fauve — forme une croûte aromatique pendant la cuisson lente. Le mélange apporte une profondeur herbacée qui souligne le gras fondant du porc.' },
          { titre: 'Blancs de poulet grillés au yaourt & herbes boisées', description: 'Le poulet marine dans yaourt, citron et Bois Fauve, ce qui attendrit la viande. Grillé à feu vif, il garde un cœur juteux et un parfum aromatique sec et équilibré.' },
          { titre: 'Côtelettes d\'agneau au cumin & oignon', description: 'Les côtelettes sont frottées d\'un mélange cumin–oignon–Bois Fauve, puis grillées pour rester rosées. Le boisé du mélange met en valeur la richesse naturelle de l\'agneau.' },
          { titre: 'Magret de canard laqué au miel & épices boisées', description: 'Le magret cuit côté peau jusqu\'à obtenir un gras croustillant, puis est laqué d\'un mélange miel–vinaigre balsamique–Bois Fauve. Le laquage gagne en profondeur grâce aux notes herbacées sèches du mélange.' },
          { titre: 'Filet de pintade aux raisins & jus aromatique', description: 'La pintade est poêlée puis déglacée au vin blanc avec raisins secs et échalote. Une pincée de Bois Fauve ajoute un parfum boisé subtil qui structure la sauce sans l\'alourdir.' },
          { titre: 'Boulettes de veau à la coriandre & oignons frits', description: 'Le haché de veau est mêlé à pain trempé, oignon frit, herbes fraîches et Bois Fauve. À la cuisson, les notes boisées s\'intègrent parfaitement à la douceur du veau.' },
          { titre: 'Travers de porc confits au miel & cumin', description: 'Les travers marinent 24 h dans ail, miel, cumin et Bois Fauve, puis cuisent à basse température jusqu\'à devenir fondants. Le mélange aromatique donne un relief herbacé très équilibré au sucré-salé.' },
          { titre: 'Tajine d\'agneau aux pruneaux & épices boisées', description: 'L\'agneau mijote longuement avec oignons, pruneaux, amandes et une pointe de Bois Fauve, qui apporte une profondeur sèche et aromatique, parfaite avec la douceur du fruit.' },
          { titre: 'Brochettes de dinde à la bière blonde', description: 'La dinde marine dans bière blonde, ail et Bois Fauve, puis est grillée rapidement. Le mélange apporte un parfum boisé qui s\'accorde naturellement avec l\'amertume légère de la bière.' },
          { titre: 'Jarret de veau braisé au jus corsé', description: 'Le jarret est saisi puis braisé au vin et aux légumes. Bois Fauve infusé dans le jus donne une structure aromatique boisée qui renforce la profondeur du braisage.' }
        ]},
        { nom: 'POISSONS', recettes: [
          { titre: 'Filets de maquereau grillés aux herbes boisées', description: 'Une marinade citron–huile d\'olive–Bois Fauve vient assouplir le maquereau sans masquer sa puissance naturelle. La cuisson vive révèle ensuite les notes herbacées et boisées du mélange.' },
          { titre: 'Saumon rôti, croûte croquante fenouil–oignons', description: 'Une croûte d\'oignons frits, fenouil et Bois Fauve apporte un contraste croustillant et aromatique à la chair grasse du saumon. Parfait servi avec des légumes vapeur pour laisser parler le mélange.' },
          { titre: 'Dos de cabillaud vapeur, panais & beurre boisé', description: 'Le cabillaud vapeur reste fondant, nappé d\'un beurre monté infusé au Bois Fauve. Le côté boisé structure la douceur du panais et donne un plat tout en délicatesse.' },
          { titre: 'Gambas sautées citron–cumin', description: 'Une marinade express citron–ail–Bois Fauve apporte une aromatique sèche et herbacée. La cuisson très vive fixe le parfum et laisse une belle fraîcheur en bouche.' },
          { titre: 'Moules au cidre & aromatiques boisées', description: 'Les moules s\'ouvrent dans cidre brut, échalote et un trait de crème. Bois Fauve ajouté hors du feu donne une profondeur herbacée qui se marie parfaitement à la douceur du cidre.' },
          { titre: 'Gratin de poisson blanc & crumble d\'épices', description: 'Le poisson effeuillé et les légumes fondants sont recouverts d\'un crumble salé parfumé à Bois Fauve. À la cuisson, le mélange libère une aromatique boisée qui structure la douceur du gratin.' },
          { titre: 'Tataki de thon en croûte poivre–fenouil', description: 'Le thon est saisi très brièvement, roulé dans un mélange poivre, fenouil et Bois Fauve. Servi froid en tranches, il révèle un parfum sec et élégant qui équilibre la force du thon cru.' },
          { titre: 'Sardines en escabèche aromatique', description: 'Les sardines poêlées marinent dans un vinaigre doux avec oignons et un soupçon de Bois Fauve. Le mélange apporte une note herbacée et boisée qui arrondit l\'acidité de l\'escabèche.' },
          { titre: 'Brandade de morue parfumée', description: 'La purée pomme de terre–morue est montée avec ail et crème, puis relevée en fin de préparation par Bois Fauve, qui apporte une profondeur herbacée cohérente avec la douceur lactée.' },
          { titre: 'Saint-Jacques snackées, topinambour & beurre boisé', description: 'Les Saint-Jacques, dorées juste ce qu\'il faut, reposent sur une purée de topinambour légèrement sucrée. Un beurre fondu au Bois Fauve apporte une touche herbacée et boisée qui complète parfaitement leur délicatesse.' }
        ]},
        { nom: 'SALADES', recettes: [
          { titre: 'Salade tiède de lentilles, carottes & oignons frits', description: 'Les lentilles vertes tièdes sont mélangées à des carottes rôties et à des oignons frits ; une vinaigrette moutarde–citron parfumée au Bois Fauve apporte une profondeur herbacée très équilibrée.' },
          { titre: 'Taboulé de boulgour au fenouil & herbes fraîches', description: 'Le boulgour gonfle à froid, puis s\'enrichit de fenouil cru finement émincé, menthe, coriandre et citron. Bois Fauve, ajouté dans la vinaigrette, donne un relief boisé subtil à l\'ensemble.' },
          { titre: 'Salade de pommes de terre & hareng fumé', description: 'Les pommes de terre tièdes accueillent des morceaux de hareng, oignons rouges et herbes. Une vinaigrette Bois Fauve apporte un parfum herbacé sec qui équilibre le gras du poisson fumé.' },
          { titre: 'Salade de pois chiches & légumes grillés', description: 'Pois chiches, courgettes et poivrons grillés se mêlent à une vinaigrette tiède parfumée à l\'ail rôti et Bois Fauve. Le mélange renforce le côté terrien des légumes.' },
          { titre: 'Salade de pâtes au poulet grillé & tomates confites', description: 'Fusilli, poulet grillé, tomates confites et roquette sont relevés d\'une huile d\'olive légèrement parfumée au Bois Fauve, qui ajoute une aromatique sèche à cette salade généreuse.' },
          { titre: 'Salade d\'endives, pommes & noix', description: 'Endives croquantes, pommes acidulées et noix grillées sont liées par une vinaigrette moutarde–miel relevée d\'une petite dose de Bois Fauve, qui met en valeur l\'amertume de l\'endive.' },
          { titre: 'Chou rouge, betterave & fromage frais', description: 'Chou rouge cru et betterave râpée se mélangent à des morceaux de fromage frais. Une pointe de Bois Fauve, intégrée dans la vinaigrette, apporte une profondeur terrienne qui équilibre la douceur des légumes.' },
          { titre: 'Salade tiède de quinoa, champignons & œufs mollets', description: 'Les champignons poêlés avec Bois Fauve se mêlent au quinoa tiède ; l\'œuf mollet ajoute le coulant qui lie le tout. Une salade forestière, chaleureuse mais légère.' },
          { titre: 'Salade de riz sauvage, saumon & légumes croquants', description: 'Riz sauvage, saumon cuit et légumes croquants s\'associent dans une salade contrastée. Une huile citronnée mêlée à Bois Fauve donne un parfum herbacé qui structure la douceur du riz.' },
          { titre: 'Salade de courge rôtie, feta & graines', description: 'La courge rôtie avec Bois Fauve devient caramélisée et parfumée. Mélangée à de la feta et des graines de tournesol, elle forme une salade chaleureuse et très équilibrée.' }
        ]},
        { nom: 'DESSERTS', recettes: [
          { titre: 'Pommes rôties au miel, noix & Bois Fauve', description: 'Les pommes évidées sont farcies de noix, miel et une pointe de Bois Fauve, puis rôties longuement pour concentrer les arômes. Le mélange apporte une profondeur boisée parfaite avec les noix.' },
          { titre: 'Compote poires–fenouil au sirop d\'érable', description: 'Les poires sont pochées doucement avec fenouil et un soupçon de Bois Fauve, puis réduites en compote. Le mélange ajoute une élégance herbacée qui équilibre la douceur de l\'érable.' },
          { titre: 'Panna cotta au lait d\'avoine & croustillant d\'oignon frit', description: 'Une panna cotta végétale au lait d\'avoine légèrement parfumée au Bois Fauve, servie avec un topping d\'oignon frit caramélisé. Un contraste sucré–salé très moderne.' },
          { titre: 'Gâteau moelleux au potimarron & épices boisées', description: 'Purée de potimarron, sucre complet et Bois Fauve créent un gâteau dense et parfumé, où le boisé sec du mélange structure la douceur du potimarron.' },
          { titre: 'Prunes rôties au vinaigre doux & Bois Fauve', description: 'Les prunes, rôties avec une touche de vinaigre de cidre et de sucre, gagnent en relief grâce à Bois Fauve, qui ajoute une aromatique herbacée raffinée.' },
          { titre: 'Granola sucré–salé à l\'avoine & Bois Fauve', description: 'Flocons d\'avoine, graines et noix torréfiées au sirop d\'érable sont relevés d\'une pointe de Bois Fauve, qui apporte une profondeur boisée parfaite pour un granola sophistiqué.' },
          { titre: 'Crème brûlée muscovado & notes boisées', description: 'La crème infuse doucement avec Bois Fauve avant cuisson, puis est recouverte d\'un caramel au sucre muscovado. Un dessert riche, terrien, avec une aromatique longue en bouche.' },
          { titre: 'Poire pochée au vin rouge, cumin doux & oignons frits', description: 'La poire pochée dans un sirop au vin rouge et cumin doux reçoit un topping d\'oignons frits. Bois Fauve, ajouté dans le sirop, apporte une dimension boisée surprenante mais harmonieuse.' },
          { titre: 'Tarte rustique pomme–fenouil', description: 'De fines lamelles de pomme et de fenouil sont disposées sur une pâte maison, saupoudrées de sucre brun et d\'une pincée de Bois Fauve. Une tarte d\'automne élégante et très aromatique.' },
          { titre: 'Crumble noisette & patate douce', description: 'Une base fondante de patate douce parfumée à Bois Fauve est recouverte d\'un crumble beurre–noisette. Le mélange renforce les notes terriennes de la patate douce et la rondeur des noisettes.' }
        ]}
      ]
    }
};

// Fonction pour obtenir toutes les catégories d'un mélange
const getToutesCategories = (melangeId) => {
  const melangeRecettes = recettes[melangeId];
  if (!melangeRecettes) return [];
  
  const emojis = {
    'DIPS': '🥣', 'ENTRÉES': '🥗', 'PLATS MIJOTÉS': '🍲', 'VIANDES': '🥩', 
    'POISSONS & FRUITS DE MER': '🐟', 'STREET FOODS': '🍔', 'DESSERTS': '🍮',
    'VIANDES BLANCHES': '🍗', 'POISSONS': '🐟', 'VÉGÉTARIEN': '🥗',
    'LÉGUMES': '🥕', 'CHAMPIGNONS': '🍄', 'SALADES': '🥬'
  };
  
  return melangeRecettes.categories.map(categorie => ({
    emoji: emojis[categorie.nom] || '👨‍🍳',
    titre: categorie.nom,
    recettes: categorie.recettes,
    couleur: melangeRecettes.couleur
  }));
};

const MelangePage = () => {
  const { melangeId } = useParams();
  const navigate = useNavigate();
  const melange = getMelangeById(melangeId);
  const [selectedEpiceId, setSelectedEpiceId] = useState(null);
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 900;

  // Appliquer la couleur d'accent dynamique
  useEffect(() => {
    const color = MELANGE_COLORS[melangeId] || '#111111';
    document.documentElement.style.setProperty('--color-melange', color);
    return () => {
      document.documentElement.style.setProperty('--color-melange', '#111111');
    };
  }, [melangeId]);

  if (!melange) {
    return (
      <div className="container section text-center">
        <Helmet>
          <title>Mélange non trouvé | KAZA</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h2>Mélange non trouvé</h2>
        <button className="btn btn-primary" onClick={() => navigate('/') }>
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${melange.nom} – Mélange d'épices | KAZA`}</title>
        <meta name="description" content={melange.description} />
        <meta property="og:title" content={`${melange.nom} – Mélange d'épices | KAZA`} />
        <meta property="og:description" content={melange.description} />
        <meta property="og:image" content={melange.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://kaza-epices.com/melange/${melange.id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${melange.nom} – Mélange d'épices | KAZA`} />
        <meta name="twitter:description" content={melange.description} />
        <meta name="twitter:image" content={melange.image} />
        {/* Schema.org Product pour le mélange */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': melange.nom,
          'description': melange.description,
          'image': `https://kaza-epices.com${melange.image}`,
          'brand': {
            '@type': 'Brand',
            'name': 'KAZA'
          },
          'category': 'Épices et mélanges d\'épices',
        })}</script>
      </Helmet>
      <div className={`fade-in melange-page-${melangeId}`}>
        {/* Fiche produit moderne */}
        <section className="section" style={{ paddingBottom: 0 }}>
          <div className="container">
            <div className="grid grid-2 melange-grid" style={{ alignItems: 'center', gap: 'var(--spacing-xxl)' }}>
              {/* Contenu à gauche */}
              <div>
                <button 
                  className="btn btn-secondary mb-3"
                  onClick={() => navigate('/')}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}
                >
                  ← Retour à l'accueil
                </button>
                <h1 className="mb-2" style={{ color: 'var(--color-melange)' }}>{melange.nom}</h1>
                <p className="mb-3" style={{ fontSize: '1.2rem', color: 'var(--color-text)' }}>{melange.description}</p>
                <div className="mb-3">
                  {melange.idealesAvec.slice(0, 4).map((item, index) => (
                    <span key={index} className="badge" style={{ background: 'var(--color-melange)', color: '#fff' }}>{item}</span>
                  ))}
                  {melange.idealesAvec.length > 4 && (
                    <span className="badge" style={{ background: 'var(--color-border)', color: 'var(--color-text-light)' }}>+{melange.idealesAvec.length - 4} autres</span>
                  )}
                </div>
                <div className="mb-4">
                  <h4 className="mb-1" style={{ color: 'var(--color-melange)' }}>Astuce du chef</h4>
                  <div className="badge" style={{ background: 'var(--color-melange)', color: '#fff', fontWeight: 600, fontSize: '1rem' }}>👨‍🍳 {melange.astuceChef}</div>
                </div>
              </div>
              {/* Image carré à droite */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <div style={{
                  width: '100%',
                  maxWidth: 340,
                  height: 340,
                  background: 'var(--color-surface)',
                  borderRadius: 24,
                  boxShadow: 'var(--shadow-md)',
                  border: '2px solid var(--color-border)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  position: 'relative',
                }}
                  className="melange-image-container"
                >
                  <ImageLoader
                    src={melange.image}
                    alt={`Mélange d'épices ${melange.nom} KAZA`}
                    fallback={
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: 'var(--color-melange)',
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: 8 }}>🧂</div>
                        <p>{melange.nom}</p>
                      </div>
                    }
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section Description Détaillée - Rouge Obsidienne */}
        {melangeId === 'rouge-obsidienne' && (
          <section className="section" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="container">
              {/* Description principale */}
              <div className="text-center mb-5">
                <p style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '600', 
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-lg)',
                  lineHeight: '1.4'
                }}>
                  Mélange fumé & piquant pour viandes, légumes rôtis et plats de caractère
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  Rouge Obsidienne est un mélange d'épices fumé, intense et légèrement pimenté, pensé pour les amateurs de saveurs puissantes et grillées. Sa base de sel noir de lave et de paprika fumé piquant apporte une profondeur volcanique, parfaite pour les cuissons à haute température.
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  La présence de poivre long de Lampong, de gingembre, de nigelle et de piment d'Amazonie vient équilibrer le tout entre chaleur, complexité aromatique et légère fraîcheur.
                </p>
              </div>

              {/* 3 blocs côte à côte */}
              <div className="grid grid-3" style={{ gap: 'var(--spacing-lg)' }}>
                {/* À quoi ça sert */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔍 À quoi ça sert ?
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    color: 'var(--color-text)',
                    marginBottom: 'var(--spacing-md)',
                    fontWeight: '600'
                  }}>
                    Ce mélange est idéal pour :
                  </p>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Sublimer les viandes rouges (bœuf, agneau) grâce à une croûte épicée irrésistible.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Relever les légumes rôtis ou grillés en leur donnant une touche fumée et épicée.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Twister des plats simples comme des œufs, des burgers ou une shakshuka.
                    </li>
                  </ul>
                </div>

                {/* Astuce du chef */}
                <div style={{ 
                  background: 'var(--color-melange)',
                  color: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(156, 1, 1, 0.2)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    color: '#fff'
                  }}>
                    🧑‍🍳 Astuce du chef
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    marginBottom: 'var(--spacing-md)',
                    color: '#fff'
                  }}>
                    Mélange 1 c. à café de Rouge Obsidienne dans de l'huile d'olive chaude ou du beurre noisette, et badigeonne ta viande avant cuisson.
                  </p>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    fontWeight: '600',
                    color: '#fff'
                  }}>
                    Résultat : croûte croustillante, arômes fumés marqués, effet waouh garanti.
                  </p>
                </div>

                {/* Conseils d'utilisation */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔧 Conseils d'utilisation
                  </h3>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Ajoute en fin de cuisson pour préserver les arômes fumés.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Parfait sur matières grasses (viandes, poissons, légumes huilés) : les épices s'y fixent mieux et développent toute leur puissance.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Dose recommandée : 1 c. à café par portion, selon ton goût.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Section Description Détaillée - Éveil Floral */}
        {melangeId === 'eveil-floral' && (
          <section className="section" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="container">
              {/* Description principale */}
              <div className="text-center mb-5">
                <p style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '600', 
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-lg)',
                  lineHeight: '1.4'
                }}>
                  Mélange citronné & floral pour poissons, viandes blanches, légumes et assaisonnements frais
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  Éveil Floral est un mélange délicat, frais et aromatique. Sa base de sel rose de l'Himalaya apporte une touche minérale subtile, rehaussée par la cardamome, le poivre de Timut, et le sumac, pour un profil légèrement citronné et floral.
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  Conçu pour sublimer sans alourdir, ce mélange est parfait sur des produits délicats : poissons, viandes blanches, légumes grillés ou plats végétariens.
                </p>
              </div>

              {/* 3 blocs côte à côte */}
              <div className="grid grid-3" style={{ gap: 'var(--spacing-lg)' }}>
                {/* À quoi ça sert */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔍 À quoi ça sert ?
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    color: 'var(--color-text)',
                    marginBottom: 'var(--spacing-md)',
                    fontWeight: '600'
                  }}>
                    Ce mélange est idéal pour :
                  </p>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Donner du relief aux plats doux ou végétariens, sans les écraser.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Apporter une fraîcheur florale aux grillades estivales.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Créer des assaisonnements légers et raffinés pour sauces, dips, salades ou plats froids.
                    </li>
                  </ul>
                </div>

                {/* Astuce du chef */}
                <div className="astuce-chef-box" style={{ 
                  background: 'var(--color-melange)',
                  color: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(114, 140, 192, 0.2)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    color: '#fff'
                  }}>
                    🧑‍🍳 Astuce du chef
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    marginBottom: 'var(--spacing-md)',
                    color: '#fff'
                  }}>
                    Ajoute une pincée d'Éveil Floral dans une sauce vierge (huile d'olive, citron, tomates concassées, herbes fraîches).
                  </p>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    fontWeight: '600',
                    color: '#fff'
                  }}>
                    Tu obtiens un assaisonnement léger, complexe et parfaitement équilibré pour les plats d'été ou les barbecues fins et détendus.
                  </p>
                </div>

                {/* Conseils d'utilisation */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔧 Conseils d'utilisation
                  </h3>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Ajoute en fin de cuisson ou juste avant de servir pour préserver les notes florales.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Parfait pour les cuissons douces ou vapeur, ou en finitions à froid.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Idéal pour des plats à base d'huile d'olive, citron, yaourt ou herbes fraîches.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Dose recommandée : 1/2 c. à café par portion, selon l'intensité souhaitée.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Section Description Détaillée - Bois Fauve */}
        {melangeId === 'bois-fauve' && (
          <section className="section" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="container">
              {/* Description principale */}
              <div className="text-center mb-5">
                <p style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '600', 
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-lg)',
                  lineHeight: '1.4'
                }}>
                  Mélange boisé, végétal & aromatique pour légumes rôtis, viandes douces, champignons et plats mijotés
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  Bois Fauve est un mélange chaleureux et équilibré, qui mêle notes boisées, herbacées et toastées. Le sel rouge d'Hawaï apporte une base minérale et douce, relevée par le poivre de Malabar, la coriandre, le cumin, le piment de la Jamaïque, le thym et le romarin.
                </p>
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--spacing-xl)',
                  maxWidth: '800px',
                  margin: '0 auto var(--spacing-xl) auto'
                }}>
                  Un mélange généreux qui évoque les sous-bois à l'automne, parfait pour sublimer des plats rustiques, gourmands ou mijotés.
                </p>
              </div>

              {/* 3 blocs côte à côte */}
              <div className="grid grid-3" style={{ gap: 'var(--spacing-lg)' }}>
                {/* À quoi ça sert */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔍 À quoi ça sert ?
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    color: 'var(--color-text)',
                    marginBottom: 'var(--spacing-md)',
                    fontWeight: '600'
                  }}>
                    Ce mélange est idéal pour :
                  </p>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Donner de la profondeur aux plats végétariens (légumes, champignons, légumineuses).
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Apporter une chaleur douce aux viandes blanches, volailles ou travers de porc.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Relever les plats d'hiver comme les soupes, gratins ou plats mijotés.
                    </li>
                  </ul>
                </div>

                {/* Astuce du chef */}
                <div className="astuce-chef-box" style={{ 
                  background: 'var(--color-melange)',
                  color: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(139, 69, 19, 0.2)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    color: '#fff'
                  }}>
                    🧑‍🍳 Astuce du chef
                  </h3>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    marginBottom: 'var(--spacing-md)',
                    color: '#fff'
                  }}>
                    Mélange Bois Fauve avec du miel, un peu de sauce soja et de l'huile d'olive pour créer une laque maison.
                  </p>
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.6',
                    fontWeight: '600',
                    color: '#fff'
                  }}>
                    Badigeonne viandes, légumes ou tofu avant cuisson pour obtenir une croûte caramélisée, boisée et ultra savoureuse.
                  </p>
                </div>

                {/* Conseils d'utilisation */}
                <div style={{ 
                  background: '#fff',
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  height: '100%'
                }}>
                  <h3 style={{ 
                    color: 'var(--color-melange)', 
                    marginBottom: 'var(--spacing-md)',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)'
                  }}>
                    🔧 Conseils d'utilisation
                  </h3>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      À utiliser avant ou pendant la cuisson, surtout en rôtissage, grill ou mijoté.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Idéal avec des plats grillés, cuits au four ou mijotés lentement.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Fonctionne à merveille avec les matières grasses (huile d'olive, beurre) pour fixer les arômes.
                    </li>
                    <li style={{ 
                      marginBottom: 'var(--spacing-sm)',
                      paddingLeft: 'var(--spacing-md)',
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: 0, 
                        color: 'var(--color-melange)',
                        fontWeight: 'bold'
                      }}>•</span>
                      Dose recommandée : 1 c. à café par portion, ajustable selon le goût recherché.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Section Composition */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2>Composition & Bienfaits</h2>
              <p style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
                Découvrez chaque ingrédient qui compose ce mélange unique et ses propriétés gustatives.
              </p>
              <div style={{
                background: 'var(--color-melange)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginTop: '16px',
                display: 'inline-block',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                👆 Cliquez sur une épice pour en savoir plus
              </div>
            </div>
            <Carousel itemsToShowDesktop={4} itemsToShowMobile={1} style={{ justifyContent: 'center' }}>
              {melange.composition.map((ingredient, index) => (
                <div key={index} className="card text-center composition-item"
                  style={{
                    cursor: 'pointer',
                    minWidth: 280,
                    maxWidth: 320,
                    margin: isMobile ? '0 auto 32px auto' : '0 auto',
                    padding: isMobile ? '28px 12px 24px 12px' : '32px 16px',
                    borderRadius: 24,
                    boxShadow: isMobile ? '0 4px 24px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.04)',
                    background: '#fff',
                    border: '1.5px solid #eee',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                  onClick={() => setSelectedEpiceId(ingredient.image.split('/').pop().replace('.jpg','').replace('.png',''))}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = isMobile ? '0 4px 24px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.04)';
                  }}
                >
                  <div style={{
                    width: isMobile ? 90 : 140,
                    height: isMobile ? 90 : 140,
                    borderRadius: '16px',
                    margin: '0 auto var(--spacing-md)',
                    overflow: 'hidden',
                    border: '3px solid var(--color-melange)'
                  }}>
                    <ImageLoader
                      src={ingredient.image}
                      alt={ingredient.nom}
                      fallback={<span style={{ fontSize: '2rem' }}>🌿</span>}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                  <h4 style={{ marginBottom: 'var(--spacing-sm)', fontSize: isMobile ? '1.1rem' : '1.15rem' }}>{ingredient.nom}</h4>
                  <p style={{ fontSize: isMobile ? '0.98rem' : '0.95rem', color: '#444', margin: 0 }}>{ingredient.bienfaits}</p>
                  
                  {/* Indicateur de clic */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'var(--color-melange)',
                    color: '#fff',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    opacity: 0.8,
                    transition: 'opacity 0.3s ease'
                  }}>
                    👆
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </section>

        {/* Section Idéal avec */}
        <section className="section" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2>Avec quoi se marie-t-il ?</h2>
              <p style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
                Ce mélange sublime particulièrement ces ingrédients et plats.
              </p>
            </div>
            <Carousel itemsToShowDesktop={3} itemsToShowMobile={1} style={{ justifyContent: 'center' }}>
              {melange.idealesAvec.map((item, index) => (
                <div key={index} className="card text-center inspiration-item" style={{ 
                  minWidth: 300, 
                  maxWidth: 380, 
                  margin: '0 auto',
                  padding: 'var(--spacing-xl)',
                  borderRadius: '20px',
                  background: '#fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: 'var(--color-melange)',
                    borderRadius: '20px',
                    margin: '0 auto var(--spacing-md)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-primary)',
                    fontSize: '3rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {item.toLowerCase() === 'viande grillée' ? '🥩'
                     : item.toLowerCase() === 'sauce tomate' ? '🍅'
                     : item.toLowerCase().includes('agneau') ? '🐑'
                     : item.toLowerCase().includes('bœuf') || item.toLowerCase().includes('steak') || item.toLowerCase().includes('burger') ? '🥩'
                     : item.toLowerCase().includes('poulet') ? '🍗'
                     : item.toLowerCase().includes('poisson') || item.toLowerCase().includes('dorade') ? '🐟'
                     : item.toLowerCase().includes('légume') ? '🥬'
                     : item.toLowerCase().includes('champignon') ? '🍄'
                     : item.toLowerCase().includes('salade') ? '🥗'
                     : item.toLowerCase().includes('tofu') ? '🧈'
                     : '🍽️'}
                  </div>
                  <h4 style={{ 
                    fontSize: '1.3rem', 
                    fontWeight: '600',
                    color: 'var(--color-text)',
                    margin: 0
                  }}>{item}</h4>
                </div>
              ))}
            </Carousel>
          </div>
        </section>


        {/* Section Idées Recettes */}
        <section className="section" style={{ backgroundColor: 'var(--color-surface)' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2>Idées Recettes Express</h2>
              <p style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
                Des idées simples et rapides pour utiliser votre mélange KAZA au quotidien.
              </p>
            </div>
            
            <div className="grid" style={{ 
              gap: 'var(--spacing-lg)',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'
            }}>
              {getToutesCategories(melangeId).map((categorie, index) => (
                <div key={index} className="card" style={{
                  padding: 'var(--spacing-lg)',
                  borderRadius: '16px',
                  background: '#fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: '1px solid var(--color-border)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                }}
                onClick={() => setSelectedCategorie(categorie)}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: categorie.couleur || 'var(--color-melange)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto var(--spacing-md)',
                    fontSize: '2rem'
                  }}>
                    {categorie.emoji}
                  </div>
                  <h3 style={{ 
                    fontSize: '1.2rem', 
                    marginBottom: 'var(--spacing-sm)',
                    color: 'var(--color-text)',
                    textAlign: 'center'
                  }}>
                    {categorie.titre}
                  </h3>
                  <p style={{ 
                    fontSize: '0.95rem', 
                    color: 'var(--color-text-light)',
                    textAlign: 'center',
                    marginBottom: 'var(--spacing-md)'
                  }}>
                    {categorie.recettes.length} idées recettes
                  </p>
                  <div style={{
                    background: categorie.couleur || 'var(--color-melange)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textAlign: 'center',
                    opacity: 0.9
                  }}>
                    Voir les recettes
                  </div>
                </div>
              ))}
            </div>
            
            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-xl)' }}>
              <button 
                className="btn btn-primary"
                onClick={() => window.open('/idees-recettes', '_blank')}
                style={{ fontSize: '1.1rem', padding: 'var(--spacing-md) var(--spacing-xl)' }}
              >
                🍽️ Voir toutes nos idées recettes
              </button>
            </div>
          </div>
        </section>

        {/* Section Navigation vers autres mélanges */}
        <section className="section">
          <div className="container">
            <div className="text-center mb-5">
              <h2>Découvrez nos autres mélanges</h2>
              <p style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
                Chaque mélange KAZA a sa propre personnalité et ses saveurs uniques.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/melange/rouge-obsidienne')}
                disabled={melangeId === 'rouge-obsidienne'}
              >
                Rouge Obsidienne
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/melange/eveil-floral')}
                disabled={melangeId === 'eveil-floral'}
              >
                Éveil Floral
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/melange/bois-fauve')}
                disabled={melangeId === 'bois-fauve'}
              >
                Bois Fauve
              </button>
            </div>
          </div>
        </section>

        {/* Modale épice */}
        {selectedEpiceId && (() => {
          const epice = getEpiceById(selectedEpiceId);
          if (!epice) return null;
          return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 'var(--spacing-md)'
            }}>
              <div style={{
                backgroundColor: 'var(--color-background)',
                borderRadius: '16px',
                padding: 'var(--spacing-xl)',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
              }}
              className="modal-content">
                <button
                  onClick={() => setSelectedEpiceId(null)}
                  style={{
                    position: 'absolute',
                    top: 'var(--spacing-md)',
                    right: 'var(--spacing-md)',
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--color-text)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-surface)'
                  }}
                  aria-label="Fermer"
                >✕</button>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ width: 140, height: 140, borderRadius: 24, overflow: 'hidden', margin: '0 auto 18px', border: '3px solid var(--color-melange)' }}>
                    <ImageLoader src={epice.image} alt={epice.nom} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <h2 style={{ color: 'var(--color-melange)', marginBottom: 8 }}>{epice.nom}</h2>
                  <div style={{ fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}><span style={{ color: 'var(--color-melange)' }}>Origine :</span> {epice.origine}</div>
                  <div style={{ color: 'var(--color-text-light)', marginBottom: 8 }}><b>Bienfaits :</b> {epice.bienfaits}</div>
                  <div style={{ fontStyle: 'italic', color: 'var(--color-text)' }}><b>Anecdote :</b> {epice.anecdote}</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Popup des recettes par catégorie */}
        {selectedCategorie && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setSelectedCategorie(null)}>
            <div style={{
              background: '#fff',
              borderRadius: '20px',
              padding: 'var(--spacing-xl)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}>
              {/* Bouton fermer */}
              <button
                onClick={() => setSelectedCategorie(null)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f0f0';
                  e.currentTarget.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#666';
                }}>
                ×
              </button>

              {/* En-tête */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 'var(--spacing-lg)',
                paddingRight: '40px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: selectedCategorie.couleur,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  marginRight: 'var(--spacing-md)'
                }}>
                  {selectedCategorie.emoji}
                </div>
                <div>
                  <h2 style={{
                    fontSize: '1.8rem',
                    fontWeight: '700',
                    color: selectedCategorie.couleur,
                    margin: 0,
                    marginBottom: '4px'
                  }}>
                    {selectedCategorie.titre}
                  </h2>
                  <p style={{
                    fontSize: '1rem',
                    color: '#666',
                    margin: 0
                  }}>
                    {selectedCategorie.recettes.length} idées recettes
                  </p>
                </div>
              </div>

              {/* Liste des recettes */}
              <div style={{
                maxHeight: '50vh',
                overflow: 'auto',
                paddingRight: '10px'
              }}>
                {selectedCategorie.recettes.map((recette, index) => {
                  const isObject = typeof recette === 'object' && recette !== null && recette.titre;
                  const titre = isObject ? recette.titre : recette.split(' – ')[0];
                  const description = isObject ? recette.description : (recette.includes(' – ') ? recette.split(' – ')[1] : '');
                  return (
                  <div key={index} style={{
                    padding: 'var(--spacing-md)',
                    border: '1px solid #eee',
                    borderRadius: '12px',
                    marginBottom: 'var(--spacing-sm)',
                    background: '#fafafa',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = selectedCategorie.couleur;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafafa';
                    e.currentTarget.style.borderColor = '#eee';
                  }}>
                      <h4 style={{
                        fontFamily: 'var(--font-display)',
                      fontSize: '1rem',
                        fontWeight: 700,
                        color: selectedCategorie.couleur,
                        textTransform: 'uppercase',
                        marginBottom: 'var(--spacing-xs)',
                        letterSpacing: '0.04em',
                        margin: '0 0 0.5rem 0'
                      }}>{titre}</h4>
                      {description && (
                        <p style={{
                          fontFamily: 'var(--font-primary)',
                          fontSize: '0.9rem',
                          color: '#666',
                      margin: 0,
                          lineHeight: '1.6'
                        }}>{description}</p>
                      )}
                  </div>
                  );
                })}
              </div>


            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MelangePage; 